/**
 * EVUS新注册(New Enrollment)完整表单组件
 *
 * 基于官方evus.gov网站的7步表单结构实现
 * 参考: doc/EVUS_FORM_SPECIFICATION.md
 *
 * 功能:
 * - 7个完整步骤的表单UI
 * - 所有30+个Step 3字段的正确实现
 * - 条件显示逻辑（如"Yes"时展开字段）
 * - "Add Another"功能（电话、社交媒体等）
 * - DD/MM/YYYY日期选择器（3个独立下拉菜单）
 * - 完整的表单验证
 * - 轮询获取处理状态
 * - （已移除）CAPTCHA验证码处理
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle,
  Loader,
  Download,
  Home,
  DollarSign,
  X,
  Plus,
} from 'lucide-react';
import { startEvusEnroll, startEvusEnrollOptimized, getEvusEnrollStatus, getEvusEnrollResult } from '../../services/evusEnroll';
import USStateSelect from '../shared/USStateSelect';
  import CopyInfoButton from './CopyInfoButton';
  // CaptchaModal 已不再使用
  import Footer from '../shared/Footer';
  import { parseCountry, normalizePhone, isValidPhone } from '../../utils/phone';
  import { normalizeLine, normalizeCity, isValidUSZip } from '../../utils/address';

// 主组件
export default function EvusEnroll() {
  const navigate = useNavigate();

  // ===== 表单状态 =====
  const [formData, setFormData] = useState({
    // Step 1: Disclaimer
    disclaimer_agreement: false,
    evus_fee_agreement: false,

    // Step 2: Travel Document Information
    travel_document: {
      surname: '',
      given_name: '',
      date_of_birth: { day: '', month: '', year: '' },
      has_10year_visa: false,
      visa_foil_number: '',
      visa_country_of_issuance: 'US',
      visa_issue_date: { day: '', month: '', year: '' },
      visa_expiry_date: { day: '', month: '', year: '' },
      passport_number: '',
      passport_country_of_issuance: 'CN',
      passport_issue_date: { day: '', month: '', year: '' },
      passport_expiry_date: { day: '', month: '', year: '' },
      passport_contains_visa: null,
      // 旧护照信息 (当passport_contains_visa=false时使用)
      visa_passport_surname: '',
      visa_passport_given_name: '',
      visa_passport_date_of_birth: { day: '', month: '', year: '' },
      visa_passport_number: '',
      visa_passport_country: 'CN',
      visa_passport_issue_date: { day: '', month: '', year: '' },
      visa_passport_expiry_date: { day: '', month: '', year: '' },
    },

    // Step 3: Enrollee Information
    applicant: {
      surname: '',
      given_name: '',
      surname_native_language: '',
      given_name_native_language: '',
      sex: 'M',
      date_of_birth: { day: '', month: '', year: '' },
      city_of_birth: '',
      country_of_birth: 'CN',
      country_of_citizenship: 'CN',
      national_identification_number: '',  // ✅ 修复：添加国民身份证号码字段 (2025-11-12)
      address_country: 'CN',
      address_line1: '',
      address_line2: '',
      apartment_number: '',
      city: '',
      state_province_region: '',
      home_address_native_language: '',
      phone_numbers: [{ phone_type: 'cell', country_code: 'CN:86', number: '' }],
      primary_email: { email: '', email_confirm: '' },
      secondary_email: { email: '', email_confirm: '' },
      has_aliases: false,
      aliases: [],
      is_citizen_of_another_country_now: false,
      has_ever_been_citizen_of_another_country: false,
      has_other_passport_or_id: false,
      has_online_presence: true,
      facebook: '',
      linkedin: '',
      twitter: '',
      instagram: '',
      social_media_profiles: [],
      no_online_presence: false,
      is_ge_member: false,
      ge_membership_number: '',
      father_surname: '',
      father_given_name: '',
      father_name_native_language: '',
      mother_surname: '',
      mother_given_name: '',
      mother_name_native_language: '',
      has_current_previous_employer: false,
      employer_name: '',
      employer_occupation: '',
      employer_address_line1: '',
      employer_city: '',
      employer_address_state: '',
      employer_country: 'CN',
    },

    // Step 4: Travel Information
    us_point_of_contact: {
      name: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'US',
      },
      phone_type: '',
      phone_country_code: 'US:1',
      phone_number: '',
    },
    us_visit_address: {
      address_line1: '',
      address_line2: '',
      city: '',
      state_province: '',
    },
    emergency_contact: {
      name: '',
      email: '',
      phone_type: 'Mobile',
      phone_country_code: 'CN:86',
      phone_number: '',
    },

    // Step 5: Eligibility Questions
    eligibility: {
      q1_physical_mental_disorder: false,
      q2_criminal_record: false,
      q3_drug_violation: false,
      q4_terrorist_activities: false,
      q5_fraud_false_statement: false,
      q6_illegal_work_us: false,
      q7_visa_denied: false,
      q8_overstayed_us: false,
      q9_dangerous_countries: false,
      // Q7附加字段（当 q7_visa_denied=true 时必填）
      q7_when_day: '',
      q7_when_month: '',
      q7_when_year: '',
      q7_where: '',
    },
  });

  // ===== UI状态 =====
  const [currentStep, setCurrentStep] = useState(0);
  const [taskId, setTaskId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingStep, setProcessingStep] = useState(0);
  const [taskStatus, setTaskStatus] = useState(null);

  // ===== 智能复用状态 =====
  const [useApplicantForUSContact, setUseApplicantForUSContact] = useState(false);
  // 移除紧急联系人来源选择，统一为手填
  // 本地兜底确认（仅前端UX，不影响后端payload）
  const [confirmUsContactPhone, setConfirmUsContactPhone] = useState(true);  // ✨ 修复问题4 (2025-12-21)：默认勾选
  const [confirmUsContactAddress, setConfirmUsContactAddress] = useState(true);  // ✨ 修复问题4 (2025-12-21)：默认勾选
  const [confirmEmergencyPhone, setConfirmEmergencyPhone] = useState(false);
  const [confirmApplicantPhone, setConfirmApplicantPhone] = useState(false);

  // （已移除）CAPTCHA状态

  // ===== 轮询状态 =====
  useEffect(() => {
    if (!taskId) return;

    const pollStatus = async () => {
      try {
        const status = await getEvusEnrollStatus(taskId);
        setProcessingStep(status.current_step || 0);
        setTaskStatus(status);

        // 后端已不再返回 captcha_waiting，不再处理验证码分支

        if (status.status === 'completed') {
          try {
            const fullResult = await getEvusEnrollResult(taskId);
            setResult(fullResult);
            setProcessing(false);
            // 更新taskStatus，包含enrollment_number
            setTaskStatus(prevStatus => ({
              ...prevStatus,
              enrollment_number: fullResult.enrollment_number
            }));
            // 如果有enrollment_number，说明支付已完成，显示Step 7结果
            if (fullResult.enrollment_number) {
              setCurrentStep(7);
            }
          } catch (err) {
            setError(`获取结果失败: ${err.message}`);
          }
          // 关键：任务完成，停止轮询
          return 'STOP_POLLING';
        } else if (status.status === 'failed') {
          setError(status.error_message || '注册失败');
          setProcessing(false);
          setTaskStatus(prevStatus => ({
            ...prevStatus,
            status: 'failed',
            error_message: status.error_message
          }));
          // 关键：任务失败，停止轮询
          return 'STOP_POLLING';
        } else if (status.status === 'processing') {
          // 根据current_step动态导航
          // Step 6以后自动显示处理界面
          if (status.current_step >= 6 && currentStep < 5) {
            setCurrentStep(5);
          }
        }
      } catch (err) {
        console.error('轮询失败:', err);
      }
    };

    let interval;
    const startPolling = async () => {
      const result = await pollStatus();
      // 如果返回STOP_POLLING，清除定时器
      if (result === 'STOP_POLLING') {
        clearInterval(interval);
        return;
      }
      // 继续轮询
      interval = setInterval(async () => {
        const result = await pollStatus();
        if (result === 'STOP_POLLING') {
          clearInterval(interval);
        }
      }, 2000);
    };

    startPolling();
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [taskId, currentStep]);

  // （已移除）CAPTCHA处理函数

  const handleDateChange = (path, field, value) => {
    const keys = path.split('.');
    const newData = { ...formData };
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]][field] = value;
    setFormData(newData);
  };

  const handleInputChange = (path, value) => {
    const keys = path.split('.');
    const newData = JSON.parse(JSON.stringify(formData));
    let current = newData;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setFormData(newData);
  };

  const addPhoneNumber = () => {
    const newData = JSON.parse(JSON.stringify(formData));
    newData.applicant.phone_numbers.push({
      phone_type: 'cell',
      country_code: 'CN:86',
      number: '',
    });
    setFormData(newData);
  };

  const removePhoneNumber = (index) => {
    const newData = JSON.parse(JSON.stringify(formData));
    newData.applicant.phone_numbers.splice(index, 1);
    setFormData(newData);
  };

  // ===== 智能复用函数（紧急联系人不再提供来源复制，统一手填）=====

  /**
   * 复制申请人信息到美国联系人
   */
  const copyApplicantToUSContact = () => {
    const newData = JSON.parse(JSON.stringify(formData));
    // 合并姓名为全名
    newData.us_point_of_contact.name =
      `${formData.applicant.surname} ${formData.applicant.given_name}`.trim();
    newData.us_point_of_contact.phone_number =
      formData.applicant.phone_numbers[0]?.number || '';
    setFormData(newData);
  };

  /**
   * 从Step 2复制旅行证件信息到Step 3申请人信息
   * 消除冗余字段输入：姓名、名字、出生日期
   */
  const copyTravelDocToApplicant = () => {
    const newData = JSON.parse(JSON.stringify(formData));
    // 复制护照中的姓名和出生日期到申请人信息
    newData.applicant.surname = newData.travel_document.surname;
    newData.applicant.given_name = newData.travel_document.given_name;
    newData.applicant.date_of_birth = JSON.parse(
      JSON.stringify(newData.travel_document.date_of_birth)
    );
    setFormData(newData);
  };

  /**
   * 处理美国联系人复用模式变化
   */
  const handleUSContactReuseChange = (checked) => {
    setUseApplicantForUSContact(checked);
    if (checked) {
      copyApplicantToUSContact();
    }
  };

  // 紧急联系人来源选择已移除

  // 当美国联系人国家码变化时，重置本地电话确认并提示重新检查号码
  const handleUsContactCountryCodeChange = (value) => {
    const newData = JSON.parse(JSON.stringify(formData));
    newData.us_point_of_contact.phone_country_code = value;
    setConfirmUsContactPhone(false);
    setFormData(newData);
  };
  // 当紧急联系人国家码变化时，重置本地电话确认
  const handleEmergencyCountryCodeChange = (value) => {
    const newData = JSON.parse(JSON.stringify(formData));
    newData.emergency_contact.phone_country_code = value;
    setConfirmEmergencyPhone(false);
    setFormData(newData);
  };

  const addSocialMedia = () => {
    const newData = JSON.parse(JSON.stringify(formData));
    newData.applicant.social_media_profiles.push({
      platform: 'Facebook',
      identifier: '',
    });
    setFormData(newData);
  };

  const removeSocialMedia = (index) => {
    const newData = JSON.parse(JSON.stringify(formData));
    newData.applicant.social_media_profiles.splice(index, 1);
    setFormData(newData);
  };

  // ✨ Phase 9: 分步验证函数 - Step 0 (免责声明)
  const validateStep0 = () => {
    if (!formData.disclaimer_agreement) {
      setError('必须同意EVUS免责声明');
      return false;
    }
    if (!formData.evus_fee_agreement) {
      setError('必须同意支付250积分');
      return false;
    }
    setError('');
    return true;
  };

  // ✨ Phase 9: 分步验证函数 - Step 1 (旅行证件)
  const validateStep1 = () => {
    setError('');

    // ===== 基础字段 (6个必填) =====
    const doc = formData.travel_document;

    if (!doc.surname || !doc.surname.trim()) {
      setError('护照姓氏必填（英文）');
      return false;
    }

    if (!doc.given_name || !doc.given_name.trim()) {
      setError('护照名字必填（英文）');
      return false;
    }

    // 出生日期必须完整 (DD/MM/YYYY)
    if (!doc.date_of_birth.day) {
      setError('出生日期的"日"必填');
      return false;
    }
    if (!doc.date_of_birth.month) {
      setError('出生日期的"月"必填');
      return false;
    }
    if (!doc.date_of_birth.year) {
      setError('出生日期的"年"必填');
      return false;
    }

    // ===== 10年签证选择 (必须选择Yes或No) =====
    if (doc.has_10year_visa === undefined || typeof doc.has_10year_visa !== 'boolean') {
      setError('请选择是否持有10年B1/B2签证');
      return false;
    }

    // ===== 如果有10年签证，验证相关字段 =====
    if (doc.has_10year_visa === true) {
      // 签证FOIL号码
      if (!doc.visa_foil_number || !doc.visa_foil_number.trim()) {
        setError('B1/B2签证FOIL号码必填（签证右上角红色号码）');
        return false;
      }

      // 护照号码
      if (!doc.passport_number || !doc.passport_number.trim()) {
        setError('护照号码必填');
        return false;
      }

      // 护照签发国家
      if (!doc.passport_country_of_issuance) {
        setError('护照签发国家必填');
        return false;
      }

      // 护照签发日期 (DD/MM/YYYY - 3个字段都必须)
      if (!doc.passport_issue_date.day) {
        setError('护照签发日期的"日"必填');
        return false;
      }
      if (!doc.passport_issue_date.month) {
        setError('护照签发日期的"月"必填');
        return false;
      }
      if (!doc.passport_issue_date.year) {
        setError('护照签发日期的"年"必填');
        return false;
      }

      // 护照过期日期 (DD/MM/YYYY - 3个字段都必须)
      if (!doc.passport_expiry_date.day) {
        setError('护照过期日期的"日"必填');
        return false;
      }
      if (!doc.passport_expiry_date.month) {
        setError('护照过期日期的"月"必填');
        return false;
      }
      if (!doc.passport_expiry_date.year) {
        setError('护照过期日期的"年"必填');
        return false;
      }

      // ✨ 新增 (2025-12-21)：验证护照过期日期必须晚于签发日期
      const issueDate = new Date(
        doc.passport_issue_date.year,
        parseInt(doc.passport_issue_date.month) - 1,
        doc.passport_issue_date.day
      );
      const expiryDate = new Date(
        doc.passport_expiry_date.year,
        parseInt(doc.passport_expiry_date.month) - 1,
        doc.passport_expiry_date.day
      );

      if (expiryDate <= issueDate) {
        setError('护照过期日期必须晚于签发日期');
        return false;
      }

      // 旅行护照是否包含美国签证 (必须选择true或false)
      if (doc.passport_contains_visa === null || typeof doc.passport_contains_visa !== 'boolean') {
        setError('请选择旅行护照是否包含美国签证');
        return false;
      }

      // ===== 如果签证在旧护照中，验证旧护照字段 =====
      if (doc.passport_contains_visa === false) {
        // 旧护照姓氏
        if (!doc.visa_passport_surname || !doc.visa_passport_surname.trim()) {
          setError('旧护照姓氏必填（英文）');
          return false;
        }

        // 旧护照名字
        if (!doc.visa_passport_given_name || !doc.visa_passport_given_name.trim()) {
          setError('旧护照名字必填（英文）');
          return false;
        }

        // 旧护照出生日期 (DD/MM/YYYY - 3个字段都必须)
        if (!doc.visa_passport_date_of_birth.day) {
          setError('旧护照出生日期的"日"必填');
          return false;
        }
        if (!doc.visa_passport_date_of_birth.month) {
          setError('旧护照出生日期的"月"必填');
          return false;
        }
        if (!doc.visa_passport_date_of_birth.year) {
          setError('旧护照出生日期的"年"必填');
          return false;
        }

        // 旧护照号码
        if (!doc.visa_passport_number || !doc.visa_passport_number.trim()) {
          setError('旧护照号码必填（持有美国签证的护照）');
          return false;
        }

        // 旧护照签发国家
        if (!doc.visa_passport_country) {
          setError('旧护照签发国家必填');
          return false;
        }

        // 旧护照签发日期 (DD/MM/YYYY)
        if (!doc.visa_passport_issue_date.day) {
          setError('旧护照签发日期的"日"必填');
          return false;
        }
        if (!doc.visa_passport_issue_date.month) {
          setError('旧护照签发日期的"月"必填');
          return false;
        }
        if (!doc.visa_passport_issue_date.year) {
          setError('旧护照签发日期的"年"必填');
          return false;
        }

        // 旧护照过期日期 (DD/MM/YYYY)
        if (!doc.visa_passport_expiry_date.day) {
          setError('旧护照过期日期的"日"必填');
          return false;
        }
        if (!doc.visa_passport_expiry_date.month) {
          setError('旧护照过期日期的"月"必填');
          return false;
        }
        if (!doc.visa_passport_expiry_date.year) {
          setError('旧护照过期日期的"年"必填');
          return false;
        }

        // ✨ 新增 (2025-12-21)：验证旧护照过期日期必须晚于签发日期
        const oldIssueDate = new Date(
          doc.visa_passport_issue_date.year,
          parseInt(doc.visa_passport_issue_date.month) - 1,
          doc.visa_passport_issue_date.day
        );
        const oldExpiryDate = new Date(
          doc.visa_passport_expiry_date.year,
          parseInt(doc.visa_passport_expiry_date.month) - 1,
          doc.visa_passport_expiry_date.day
        );

        if (oldExpiryDate <= oldIssueDate) {
          setError('旧护照过期日期必须晚于签发日期');
          return false;
        }
      }
    }

    return true;
  };

  // ✨ Phase 9: 分步验证函数 - Step 2 (申请人信息)
  const validateStep2 = () => {
    setError('');
    const applicant = formData.applicant;

    // ===== 个人基本信息 (7个必填) =====
    if (!applicant.surname || !applicant.surname.trim()) {
      setError('申请人姓氏必填');
      return false;
    }

    if (!applicant.given_name || !applicant.given_name.trim()) {
      setError('申请人名字必填');
      return false;
    }

    // ===== 中文姓名验证 (2个必填) =====
    if (!applicant.surname_native_language || !applicant.surname_native_language.trim()) {
      setError('姓（汉字）必填');
      return false;
    }

    if (!applicant.given_name_native_language || !applicant.given_name_native_language.trim()) {
      setError('名（汉字）必填');
      return false;
    }

    if (!applicant.sex) {
      setError('性别必填');
      return false;
    }

    // 出生日期必须完整
    if (!applicant.date_of_birth.day) {
      setError('出生日期的"日"必填');
      return false;
    }
    if (!applicant.date_of_birth.month) {
      setError('出生日期的"月"必填');
      return false;
    }
    if (!applicant.date_of_birth.year) {
      setError('出生日期的"年"必填');
      return false;
    }

    if (!applicant.city_of_birth || !applicant.city_of_birth.trim()) {
      setError('出生城市必填');
      return false;
    }

    if (!applicant.country_of_birth) {
      setError('出生国家必填');
      return false;
    }

    if (!applicant.country_of_citizenship) {
      setError('国籍必填');
      return false;
    }

    // ===== 地址信息 (4个必填) =====
    if (!applicant.address_country) {
      setError('住址国家必填');
      return false;
    }

    if (!applicant.address_line1 || !applicant.address_line1.trim()) {
      setError('住址行1必填');
      return false;
    }

    if (!applicant.city || !applicant.city.trim()) {
      setError('城市必填');
      return false;
    }

    if (!applicant.state_province_region || !applicant.state_province_region.trim()) {
      setError('州/省/地区必填');
      return false;
    }

    // ===== 联系信息 (3个必填) =====
    if (!applicant.phone_numbers || !applicant.phone_numbers[0] || !applicant.phone_numbers[0].number) {
      setError('电话号码必填');
      return false;
    }
    // 申请人电话号码格式（基于国家码的轻量校验，可由“确认号码正确”覆盖）
    try {
      const apPhone = applicant.phone_numbers[0];
      const iso2 = parseCountry(apPhone.country_code).iso2 || 'CN';
      const ok = isValidPhone(apPhone.number, iso2);
      if (!ok && !confirmApplicantPhone) {
        setError('申请人电话号码格式可能无效，如确认无误可勾选“我确认该电话号码正确”后再提交');
        return false;
      }
    } catch (e) {
      // 忽略本地校验异常
    }

    if (!applicant.primary_email.email || !applicant.primary_email.email.trim()) {
      setError('邮箱必填');
      return false;
    }

    if (applicant.primary_email.email !== applicant.primary_email.email_confirm) {
      setError('邮箱确认不一致');
      return false;
    }

    // ✨ Phase 9: 邮箱格式验证
    if (!validateEmailFormat(applicant.primary_email.email)) {
      setError('邮箱格式无效，请检查邮箱地址');
      return false;
    }

    // ===== 家庭信息 (4个必填) =====
    if (!applicant.father_surname || !applicant.father_surname.trim()) {
      setError('父亲姓氏必填');
      return false;
    }

    if (!applicant.father_given_name || !applicant.father_given_name.trim()) {
      setError('父亲名字必填');
      return false;
    }

    if (!applicant.mother_surname || !applicant.mother_surname.trim()) {
      setError('母亲姓氏必填');
      return false;
    }

    if (!applicant.mother_given_name || !applicant.mother_given_name.trim()) {
      setError('母亲名字必填');
      return false;
    }

    return true;
  };

  // ✨ Phase 9: 分步验证函数 - Step 3 (旅行信息)
  const validateStep3 = () => {
    setError('');
    const usContact = formData.us_point_of_contact;
    const emergencyContact = formData.emergency_contact;

    // ===== 美国联系人 (必填项) =====
    if (!usContact.name || !usContact.name.trim()) {
      setError('美国联系人姓名必填');
      return false;
    }

    if (!usContact.address.address_line1 || !usContact.address.address_line1.trim()) {
      setError('美国联系人地址行1必填');
      return false;
    }

    if (!usContact.address.city || !usContact.address.city.trim()) {
      setError('美国联系人城市必填');
      return false;
    }

    if (!usContact.address.state_province) {
      setError('美国联系人州必填');
      return false;
    }

    if (!usContact.address.postal_code || !usContact.address.postal_code.trim()) {
      setError('美国联系人邮编必填');
      return false;
    }

    // ✨ 修复 (2025-12-21, Phase 11.11.8)：删除phone_type验证（后端自动填充"Mobile"）

    if (!usContact.phone_country_code) {
      setError('美国联系人电话国家代码必填');
      return false;
    }

    if (!usContact.phone_number || !usContact.phone_number.trim()) {
      setError('美国联系人电话号码必填');
      return false;
    }
    // 使用国家码+号码的本地验证（优先 US 规则）
    const usCc = parseCountry(usContact.phone_country_code).iso2 || 'US';
    const usPhoneOk = isValidPhone(usContact.phone_number, usCc);
    if (!usPhoneOk && !confirmUsContactPhone) {
      setError('美国联系人电话号码格式可能无效，如确认无误可勾选"我确认该电话号码正确"后再提交');
      return false;
    }

    // ===== 紧急联系人 (必填项) =====
    if (!emergencyContact.name || !emergencyContact.name.trim()) {
      setError('紧急联系人姓名必填');
      return false;
    }

    // ✨ 修复 (2025-12-21, Phase 11.11.8)：删除phone_type验证（后端自动填充"Mobile"）

    if (!emergencyContact.phone_country_code) {
      setError('紧急联系人电话国家代码必填');
      return false;
    }

    if (!emergencyContact.phone_number || !emergencyContact.phone_number.trim()) {
      setError('紧急联系人电话号码必填');
      return false;
    }

    // ✨ Phase 9: 紧急联系人电话号码格式验证（根据国家码动态校验）
    {
      const ecIso2 = parseCountry(emergencyContact.phone_country_code).iso2 || 'US';
      const ecPhoneOk = isValidPhone(emergencyContact.phone_number, ecIso2);
      if (!ecPhoneOk && !confirmEmergencyPhone) {
        setError('紧急联系人电话号码格式可能无效，如确认无误可勾选"我确认该电话号码正确"后再提交');
        return false;
      }
    }

    // ===== 美国联系人地址的 ZIP 基础校验（仅 US） =====
    const zipOk = isValidUSZip(usContact.address.postal_code || '');
    if (!zipOk && !confirmUsContactAddress) {
      setError('美国联系人邮编格式可能无效（应为5位或ZIP+4），如确认无误可勾选"我确认该地址正确"后提交');
      return false;
    }

    return true;
  };

  // ✨ Phase 9: 分步验证函数 - Step 4 (资格问题)
  const validateStep4 = () => {
    setError('');
    const eligibility = formData.eligibility;

    // ===== 9个资格问题必须全部回答 =====
    const questions = [
      { key: 'q1_physical_mental_disorder', name: '资格问题1（身体/精神障碍）' },
      { key: 'q2_criminal_record', name: '资格问题2（犯罪记录）' },
      { key: 'q3_drug_violation', name: '资格问题3（毒品违法）' },
      { key: 'q4_terrorist_activities', name: '资格问题4（恐怖/间谍活动）' },
      { key: 'q5_fraud_false_statement', name: '资格问题5（欺诈/虚假陈述）' },
      { key: 'q6_illegal_work_us', name: '资格问题6（非法工作）' },
      { key: 'q7_visa_denied', name: '资格问题7（签证被拒）' },
      { key: 'q8_overstayed_us', name: '资格问题8（逾期停留）' },
      { key: 'q9_dangerous_countries', name: '资格问题9（危险国家）' },
    ];

    // 检查每个问题是否被回答（即不是undefined）
    for (const question of questions) {
      if (eligibility[question.key] === undefined || typeof eligibility[question.key] !== 'boolean') {
        setError(`${question.name}必须回答`);
        return false;
      }
    }

    // 当 Q7=Yes 时，要求填写 When/Where 附加字段
    if (eligibility.q7_visa_denied === true) {
      if (!eligibility.q7_when_day || !eligibility.q7_when_month || !eligibility.q7_when_year) {
        setError('请选择签证被拒的日期（日/月/年）');
        return false;
      }
      if (!eligibility.q7_where || !eligibility.q7_where.trim()) {
        setError('请输入签证被拒的地点（Where）');
        return false;
      }
    }

    return true;
  };

  // ✨ Phase 9: 格式验证函数 - 邮箱格式检查
  const validateEmailFormat = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // ✨ Phase 9: 格式验证函数 - 电话号码格式检查（仅允许数字和特定符号）
  const validatePhoneFormat = (phone) => {
    // 允许数字、+、-、()、空格
    const phoneRegex = /^[\d+\-() ]+$/;
    return phoneRegex.test(phone);
  };

  const validateForm = () => {
    // ✨ Phase 9: 增强的全量验证 - 调用所有步骤验证函数确保完整性
    setError('');

    // Step 0: 免责声明验证
    if (!validateStep0()) {
      return false;
    }

    // Step 1: 旅行证件验证
    if (!validateStep1()) {
      return false;
    }

    // Step 2: 申请人信息验证
    if (!validateStep2()) {
      return false;
    }

    // Step 3: 旅行信息验证
    if (!validateStep3()) {
      return false;
    }

    // Step 4: 资格问题验证
    if (!validateStep4()) {
      return false;
    }

    // ===== 额外的综合验证 =====
    // 验证日期逻辑（出生日期不能晚于护照签发日期）
    const dob = formData.travel_document.date_of_birth;
    const passIssue = formData.travel_document.passport_issue_date;

    if (dob.year && passIssue.year && dob.year >= passIssue.year) {
      setError('出生年份必须早于护照签发年份');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');

    // 申请人电话基础格式校验（本地），允许勾选“确认号码正确”跳过
    try {
      const apPhone = formData.applicant?.phone_numbers?.[0];
      if (apPhone && apPhone.number) {
        const iso2 = parseCountry(apPhone.country_code).iso2 || 'CN';
        const ok = isValidPhone(apPhone.number, iso2);
        if (!ok && !confirmApplicantPhone) {
          setError('申请人电话号码格式可能无效，如确认无误可勾选“我确认该电话号码正确”后再提交');
          return;
        }
      }
    } catch (e) {
      // 忽略本地校验异常，继续后端兜底
    }

    // ✨ Phase 9: 在提交前检查Step 4验证
    if (!validateStep4()) {
      return;
    }

    // 完整表单验证（所有步骤的综合验证）
    if (!validateForm()) {
      return;
    }

    // 提交前对关键字段做轻量规范化（不改变用户UI，只影响提交）
    const normalizedFormData = JSON.parse(JSON.stringify(formData));
    // 申请人电话：数组内每个号码只保留数字
    if (Array.isArray(normalizedFormData.applicant?.phone_numbers)) {
      normalizedFormData.applicant.phone_numbers = normalizedFormData.applicant.phone_numbers.map(p => ({
        ...p,
        number: normalizePhone(p.number || '')
      }));
    }
    // 美国联系人电话：只保留数字
    normalizedFormData.us_point_of_contact.phone_number = normalizePhone(normalizedFormData.us_point_of_contact.phone_number || '');
    // 紧急联系人电话：只保留数字
    normalizedFormData.emergency_contact.phone_number = normalizePhone(normalizedFormData.emergency_contact.phone_number || '');
    // 紧急联系人：拆分name为surname和given_name
    if (normalizedFormData.emergency_contact?.name) {
      const nameParts = normalizedFormData.emergency_contact.name.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        normalizedFormData.emergency_contact.surname = nameParts[0];
        normalizedFormData.emergency_contact.given_name = nameParts.slice(1).join(' ');
      } else {
        // 如果只有一个词，作为姓氏
        normalizedFormData.emergency_contact.surname = nameParts[0] || '';
        normalizedFormData.emergency_contact.given_name = '';
      }
    }
    // 美国联系人地址：转大写与清理
    normalizedFormData.us_point_of_contact.address.address_line1 =
      normalizeLine(normalizedFormData.us_point_of_contact.address.address_line1 || '');
    normalizedFormData.us_point_of_contact.address.address_line2 =
      normalizeLine(normalizedFormData.us_point_of_contact.address.address_line2 || '');
    normalizedFormData.us_point_of_contact.address.city =
      normalizeCity(normalizedFormData.us_point_of_contact.address.city || '');
    normalizedFormData.us_point_of_contact.address.postal_code =
      (normalizedFormData.us_point_of_contact.address.postal_code || '').trim();

    setSubmitting(true);
    setProcessing(true);

    try {
      // ✨ 数据清洗：处理日期字段类型转换
      const cleanDateInput = (dateObj) => {
        // 如果为null或所有字段都为空,返回null
        if (!dateObj || (!dateObj.day && !dateObj.month && !dateObj.year)) {
          return null;
        }
        // 转换为整数（空字符串变为null）
        return {
          day: dateObj.day ? parseInt(dateObj.day) : null,
          month: dateObj.month ? parseInt(dateObj.month) : null,
          year: dateObj.year ? parseInt(dateObj.year) : null,
        };
      };

      // 构建优化版表单数据
      const optimizedFormData = {
        // Step 1: 协议同意
        disclaimer_agreement: normalizedFormData.disclaimer_agreement,
        evus_fee_agreement: normalizedFormData.evus_fee_agreement,

        // Step 2: 旅行文件信息 (✨ 清洗日期字段)
        travel_document: {
          ...normalizedFormData.travel_document,
          date_of_birth: cleanDateInput(normalizedFormData.travel_document.date_of_birth),
          visa_issue_date: normalizedFormData.travel_document.has_10year_visa
            ? cleanDateInput(normalizedFormData.travel_document.visa_issue_date)
            : null,  // 如果没有10年签证，强制为null
          visa_expiry_date: normalizedFormData.travel_document.has_10year_visa
            ? cleanDateInput(normalizedFormData.travel_document.visa_expiry_date)
            : null,
          passport_issue_date: cleanDateInput(normalizedFormData.travel_document.passport_issue_date),
          passport_expiry_date: cleanDateInput(normalizedFormData.travel_document.passport_expiry_date),
          // 旧护照日期清洗 (当passport_contains_visa=false时使用)
          visa_passport_issue_date: normalizedFormData.travel_document.passport_contains_visa === false
            ? cleanDateInput(normalizedFormData.travel_document.visa_passport_issue_date)
            : null,
          visa_passport_expiry_date: normalizedFormData.travel_document.passport_contains_visa === false
            ? cleanDateInput(normalizedFormData.travel_document.visa_passport_expiry_date)
            : null,
          // ✨ 新增：旧护照个人信息字段（显式处理，确保正确传递）
          visa_passport_dob: normalizedFormData.travel_document.passport_contains_visa === false
            ? cleanDateInput(normalizedFormData.travel_document.visa_passport_date_of_birth)
            : null,
          visa_passport_surname: normalizedFormData.travel_document.passport_contains_visa === false
            ? normalizedFormData.travel_document.visa_passport_surname
            : null,
          visa_passport_given_name: normalizedFormData.travel_document.passport_contains_visa === false
            ? normalizedFormData.travel_document.visa_passport_given_name
            : null,
        },

        // Step 3: 申请人信息（用户只填一次）
        applicant_surname: normalizedFormData.applicant.surname,
        applicant_given_name: normalizedFormData.applicant.given_name,
        applicant_surname_native_language: normalizedFormData.applicant.surname_native_language,
        applicant_given_name_native_language: normalizedFormData.applicant.given_name_native_language,
        sex: normalizedFormData.applicant.sex,
        date_of_birth: cleanDateInput(normalizedFormData.applicant.date_of_birth),
        city_of_birth: normalizedFormData.applicant.city_of_birth,
        country_of_birth: normalizedFormData.applicant.country_of_birth,
        country_of_citizenship: normalizedFormData.applicant.country_of_citizenship,
        national_identification_number: normalizedFormData.applicant.national_identification_number,  // ✨ 修复：在提交时包含身份证号码字段 (2025-11-13)
        contact_info: {
          address_country: normalizedFormData.applicant.address_country,
          address_line1: normalizedFormData.applicant.address_line1,
          address_line2: normalizedFormData.applicant.address_line2,
          apartment_number: normalizedFormData.applicant.apartment_number,
          city: normalizedFormData.applicant.city,
          state_province_region: normalizedFormData.applicant.state_province_region,
          phone_numbers: normalizedFormData.applicant.phone_numbers,
          primary_email: normalizedFormData.applicant.primary_email,
          secondary_email: normalizedFormData.applicant.secondary_email.email ? normalizedFormData.applicant.secondary_email : null,
        },

        // Step 3 可选字段
        has_aliases: normalizedFormData.applicant.has_aliases,
        aliases: normalizedFormData.applicant.aliases,
        is_citizen_of_another_country_now: normalizedFormData.applicant.is_citizen_of_another_country_now,
        has_ever_been_citizen_of_another_country: normalizedFormData.applicant.has_ever_been_citizen_of_another_country,
        has_other_passport_or_id: normalizedFormData.applicant.has_other_passport_or_id,
        has_online_presence: normalizedFormData.applicant.has_online_presence,
        social_media_profiles: normalizedFormData.applicant.social_media_profiles,
        no_online_presence: normalizedFormData.applicant.no_online_presence,
        is_ge_member: normalizedFormData.applicant.is_ge_member,
        ge_membership_number: normalizedFormData.applicant.ge_membership_number,
        father_surname: normalizedFormData.applicant.father_surname,
        father_given_name: normalizedFormData.applicant.father_given_name,
        father_name_native_language: normalizedFormData.applicant.father_name_native_language,
        mother_surname: normalizedFormData.applicant.mother_surname,
        mother_given_name: normalizedFormData.applicant.mother_given_name,
        mother_name_native_language: normalizedFormData.applicant.mother_name_native_language,
        has_current_previous_employer: normalizedFormData.applicant.has_current_previous_employer,
        employer_name: normalizedFormData.applicant.employer_name,
        employer_occupation: normalizedFormData.applicant.employer_occupation,
        employer_address_line1: normalizedFormData.applicant.employer_address_line1,
        employer_city: normalizedFormData.applicant.employer_city,
        employer_address_state: normalizedFormData.applicant.employer_address_state,
        employer_country: normalizedFormData.applicant.employer_country,
        us_contact: {
          use_applicant_info: useApplicantForUSContact,
          name: normalizedFormData.us_point_of_contact.name,
          address_line1: normalizedFormData.us_point_of_contact.address.address_line1,
          address_line2: normalizedFormData.us_point_of_contact.address.address_line2,
          city: normalizedFormData.us_point_of_contact.address.city,
          state_province: normalizedFormData.us_point_of_contact.address.state_province,
          postal_code: normalizedFormData.us_point_of_contact.address.postal_code,
          phone_country_code: normalizedFormData.us_point_of_contact.phone_country_code,
          phone_number: normalizedFormData.us_point_of_contact.phone_number,
        },
        emergency_contact: {
          source: 'custom', // 统一手填
          surname: normalizedFormData.emergency_contact.surname,
          given_name: normalizedFormData.emergency_contact.given_name,
          email: normalizedFormData.emergency_contact.email || null,
          phone_country_code: normalizedFormData.emergency_contact.phone_country_code,
          phone_number: normalizedFormData.emergency_contact.phone_number,
        },

        // Step 5: 资格问题
        eligibility: formData.eligibility,
      };

      // 调用优化版API
      const response = await startEvusEnrollOptimized(optimizedFormData);
      setTaskId(response.task_id);

      // 导航到Step 5（审核）
      setCurrentStep(5);
      setSubmitting(false);

      // useEffect会自动监听taskId变化并启动轮询
    } catch (err) {
      setError(err.message || '提交失败');
      setSubmitting(false);
      setProcessing(false);
    }
  };

  // ===== 显示结果 =====
  if (result) {
	    return (
	      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-4">
	        <div className="max-w-4xl mx-auto">
	          <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
	            <div className="text-center mb-6">
	              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
	                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">注册成功!</h1>
              <p className="text-gray-600">您的EVUS新登记申请已成功提交</p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">您的登记号:</p>
	              <p className="text-3xl font-bold text-blue-600 font-mono">
	                {result.enrollment_number}
	              </p>
	            </div>

	            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
	              <p className="text-sm text-amber-900">
	                <strong>隐私提示：</strong>本服务后台不保存任何EVUS登记记录，请立即保存登记号并下载确认页截图。
	              </p>
	            </div>

	            {result.screenshot_base64 && (
	              <div className="mb-6">
	                <h3 className="text-lg font-semibold text-gray-900 mb-3">确认页面截图</h3>
	                <img
	                  src={`data:image/png;base64,${result.screenshot_base64}`}
                  alt="EVUS confirmation"
                  className="w-full border border-gray-300 rounded-lg shadow-md"
                />
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                <Home className="w-4 h-4 mr-2" />
                返回首页
              </button>

              {result.screenshot_base64 && (
                <a
                  href={`data:image/png;base64,${result.screenshot_base64}`}
                  download={`EVUS_${result.enrollment_number}_${new Date().toISOString().slice(0, 10)}.png`}
                  className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  下载截图
                </a>
              )}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-3">下一步:</h3>
            <ol className="list-decimal list-inside text-blue-800 space-y-2">
              <li>登记号有效期为120天,请在此期间完成付款</li>
              <li>支付费用: USD $30.00</li>
              <li>支付成功后,登记状态将更新为"已批准"</li>
              <li>保留确认页面和登记号,以备将来查询</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  // ===== 显示处理中 =====
  // 关键：先检查最终状态（failed/completed），只有当processing且status为processing时才显示loading
  if (processing && taskStatus?.status === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-4">
        {/* CAPTCHA 流程已移除 */}

        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <Loader className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-bold text-gray-900 mb-4">处理中...</h2>
          <div className="space-y-2">
            <p className="text-gray-600">正在自动填写和提交您的EVUS登记表单</p>
            <p className="text-sm text-gray-500">步骤 {processingStep}/7</p>
          </div>

	          <div className="mt-6 bg-gray-200 rounded-full h-2">
	            <div
	              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
	              style={{ width: `${(processingStep / 7) * 100}%` }}
	            />
	          </div>

	          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-left">
	            <p className="text-xs text-amber-900">
	              <strong>隐私提示：</strong>本服务后台不保存任何EVUS登记记录，完成后请立即保存登记号并下载确认页截图。
	            </p>
	          </div>

	          <p className="text-xs text-gray-500 mt-4">
	            这个过程通常需要2-3分钟,请不要关闭此页面
	          </p>

          {/* CAPTCHA 流程已移除，无需额外提示 */}
        </div>
      </div>
    );
  }

  // ===== 显示最终结果（completed/failed）=====
  if (taskStatus?.status === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-center mb-4">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">EVUS注册成功！</h2>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">您的EVUS登记号:</p>
            <p className="text-2xl font-mono font-bold text-green-700 my-2 bg-white p-3 rounded text-center">
              {taskStatus.enrollment_number}
            </p>
            <p className="text-xs text-gray-500 text-center">
              请妥善保存此登记号，有效期2年
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => window.print()}
              className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
            >
              打印/保存
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (taskStatus?.status === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="flex justify-center mb-4">
            <AlertCircle className="w-16 h-16 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">注册失败</h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-700">
              {taskStatus.error_message || error || '注册过程中出现错误，请稍后重试或联系客服'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                setTaskId(null);
                setProcessing(false);
                setCurrentStep(0);
                setError(null);
              }}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              重新填写
            </button>
            <button
              onClick={() => navigate('/')}
              className="flex-1 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== 显示表单 =====
  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
	        {/* 进度指示器 */}
	        <div className="mb-8">
	          <h1 className="text-4xl font-bold text-gray-900 mb-2">EVUS新登记申请</h1>
	          <p className="text-gray-600 mb-3">
	            第 {currentStep + 1} 步 / 共 5 步
	          </p>
	          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
	            <p className="text-sm text-amber-900">
	              <strong>隐私提示：</strong>本服务后台不保存任何EVUS登记记录。提交完成后请务必保存登记号并下载确认页截图。
	            </p>
	          </div>
	          <div className="flex gap-2">
	            {[0, 1, 2, 3, 4].map((step) => (
	              <div
	                key={step}
	                className={`flex-1 h-2 rounded-full transition-colors ${
                  step <= currentStep ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Step 1: Disclaimer */}
        {currentStep === 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-4 border-b">
              Step 1: 免责声明
            </h2>

            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700">
                  请阅读以下免责声明，并确认您同意这些条款才能继续。
                </p>
              </div>

              {/* 免责声明正文 - 第一条 */}
              {/* TODO(i18n): 如后续引入多语言或集中化文案管理，请将以下静态文案提取到资源文件。 */}
              <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">第一条</h3>
                <div className="space-y-3 text-sm text-gray-700 leading-6">
                  <p>
                    本签证更新电子系统（EVUS）与执法部门 的数据库进行比对。所有持10年有效期访客签证的旅客（B1, B2 或 B1/B2）, 如果需要以短期访客身份入境美国，都必须在赴美前在EVUS上登记。
                  </p>
                  <p>
                    如果您的EVUS登记获得批准，就意味着您可以前往美国。但这并不意味着您可以入境美国。抵达美国入境口岸时，您将接受United States Customs and Border Protection官员的检查。 入境口岸的官员会根据美国移民法的规定决定您是否可以入境。
                  </p>
                  <p>
                    由您或指定的第三方代理人所提供的所有信息，必须是真实和正确的。在EVUS上的登记可能会因违反美国移民法而随时被撤销。如果您在EVUS上登记时由您或您的代理人有意提交任何虚假、虚构或伪造的声明或陈述，您可能会因此受到行政或刑事处罚。
                  </p>
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                    <p className="text-sm text-red-800">
                      <strong>警告：</strong>
                      如果您在入境口岸是以短期访客的身份申请入境美国，在访问期间，您不可以接受未经授权的就业，或者上学，或代表外国的资讯媒体。
                    </p>
                  </div>
                </div>
              </section>

              {/* 免责声明正文 - 第二条（费用） */}
              {/* TODO(i18n): 如后续引入多语言或集中化文案管理，请将以下静态文案提取到资源文件。 */}
              <section className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">第二条</h3>
                <div className="space-y-3 text-sm text-gray-700 leading-6">
                  <p>
                    美国国会通过 One Big Beautiful Bill Act (PL 119-21) 规定，每次 EVUS 登记需缴纳 30 美元的费用。 对于每一次前往美国的 EVUS 登记，无论登记是否成功，U.S. Customs and Border Protection (CBP) 都将收取30美元的费用，用于处理该次 EVUS 登记及系统维护。付款之前，登记者可以保存登记数据，稍后再返回登记处更新信息和/或输入付款信息。 更新注册不会收取额外费用。 该费用只能通过信用卡 支付。 仅当所有付款信息完成后，才会提交注册以供处理。
                  </p>
                  <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5" />
                    <p className="text-sm text-red-800">
                      <strong>警告：</strong>
                      管理费将通过信用卡 支付。 登记者和/或指定的第三方准确输入 EVUS 和付款信息至关重要。 如果输入的信息不正确，登记者将被收取额外的费用才能再次登记。 未完成付款流程且未成功注册的旅客将不被允许登上前往美国的飞机或船只。如果旅行者停止支付费用，其前往美国的登记将被撤销。 CBP 对于任何交易中信用卡公司 可能收取的额外费用不承担责任。通过按下“登记”按钮在 EVUS 上登记，登记者同意不对 Customs and Border Protection (CBP) 因使用 EVUS 而收取的任何行政费用提出异议，并进一步确认不予退款。
                    </p>
                  </div>
                </div>
              </section>

              <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.disclaimer_agreement}
                  onChange={(e) => handleInputChange('disclaimer_agreement', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 mt-1"
                />
                <span className="text-gray-700">
                  我已阅读了以上两条信息，并同意这些条款
                </span>
              </label>

              <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.evus_fee_agreement}
                  onChange={(e) => handleInputChange('evus_fee_agreement', e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 mt-1"
                />
                <span className="text-gray-700">
                  我同意支付250积分
                </span>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                返回首页
              </button>
              <button
                onClick={() => {
                  if (validateStep0()) {
                    setCurrentStep(1);
                  }
                }}
                disabled={!formData.disclaimer_agreement || !formData.evus_fee_agreement}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
              >
                继续
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Travel Document */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-4 border-b">
              Step 2: 旅行证件信息
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    护照姓氏 *
                  </label>
                  <input
                    type="text"
                    value={formData.travel_document.surname}
                    onChange={(e) => handleInputChange('travel_document.surname', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="护照中的姓氏（英文）"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    护照名字 *
                  </label>
                  <input
                    type="text"
                    value={formData.travel_document.given_name}
                    onChange={(e) => handleInputChange('travel_document.given_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="护照中的名字（英文）"
                  />
                </div>
              </div>

              {/* 出生日期 DD/MM/YYYY */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  出生日期 (DD/MM/YYYY) *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <select
                      value={formData.travel_document.date_of_birth.day}
                      onChange={(e) =>
                        handleDateChange('travel_document.date_of_birth', 'day', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">日</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      value={formData.travel_document.date_of_birth.month}
                      onChange={(e) =>
                        handleDateChange('travel_document.date_of_birth', 'month', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">月</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <select
                      value={formData.travel_document.date_of_birth.year}
                      onChange={(e) =>
                        handleDateChange('travel_document.date_of_birth', 'year', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">年</option>
                      {Array.from({ length: 125 }, (_, i) => {
                        const year = 2025 - i;
                        return (
                          <option key={year} value={String(year)}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              </div>

              {/* 是否有10年B1/B2签证 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  您是否有10年商务或旅游(B1/B2)签证? *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_10year_visa"
                      checked={formData.travel_document.has_10year_visa === true}
                      onChange={() => handleInputChange('travel_document.has_10year_visa', true)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-gray-700">是</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="has_10year_visa"
                      checked={formData.travel_document.has_10year_visa === false}
                      onChange={() => handleInputChange('travel_document.has_10year_visa', false)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-gray-700">否</span>
                  </label>
                </div>
              </div>

              {/* B1/B2签证警告提示 - 当选择"否"时显示 */}
              {formData.travel_document.has_10year_visa === false && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded mt-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                      <strong>重要提示：</strong>EVUS登记仅适用于持有10年期B1/B2签证的人员。如果您没有此类签证，无需继续填写。
                    </div>
                  </div>
                </div>
              )}

              {/* 条件显示: 如果有10年签证，显示相关字段 */}
              {formData.travel_document.has_10year_visa && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                  <h3 className="font-semibold text-blue-900">B1/B2签证信息</h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      签证FOIL号码 *
                    </label>
                    <input
                      type="text"
                      value={formData.travel_document.visa_foil_number}
                      onChange={(e) =>
                        handleInputChange('travel_document.visa_foil_number', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="签证右上角的红色号码"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      护照号码 *
                    </label>
                    <input
                      type="text"
                      value={formData.travel_document.passport_number}
                      onChange={(e) =>
                        handleInputChange('travel_document.passport_number', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="旅行时携带的护照号码"
                    />
                  </div>

                  {/* 护照签发国家 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      护照签发国家 *
                    </label>
                    <select
                      value={formData.travel_document.passport_country_of_issuance || 'CN'}
                      onChange={(e) =>
                        handleInputChange('travel_document.passport_country_of_issuance', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">选择国家</option>
                      <option value="CN">中国</option>
                    </select>
                  </div>

                  {/* 护照签发日期 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      护照签发日期 (DD/MM/YYYY) *
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <select
                        value={formData.travel_document.passport_issue_date?.day || ''}
                        onChange={(e) =>
                          handleDateChange('travel_document.passport_issue_date', 'day', e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">日</option>
                        {Array.from({ length: 31 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>

                      <select
                        value={formData.travel_document.passport_issue_date?.month || ''}
                        onChange={(e) =>
                          handleDateChange('travel_document.passport_issue_date', 'month', e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">月</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>

                      <select
                        value={formData.travel_document.passport_issue_date?.year || ''}
                        onChange={(e) =>
                          handleDateChange('travel_document.passport_issue_date', 'year', e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">年</option>
                        {Array.from({ length: 50 }, (_, i) => {
                          const year = 2025 - i;
                          return (
                            <option key={year} value={String(year)}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* 护照有效期 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      护照有效期 (DD/MM/YYYY) *
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <select
                        value={formData.travel_document.passport_expiry_date.day}
                        onChange={(e) =>
                          handleDateChange('travel_document.passport_expiry_date', 'day', e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">日</option>
                        {Array.from({ length: 31 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.travel_document.passport_expiry_date.month}
                        onChange={(e) =>
                          handleDateChange('travel_document.passport_expiry_date', 'month', e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">月</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.travel_document.passport_expiry_date.year}
                        onChange={(e) =>
                          handleDateChange('travel_document.passport_expiry_date', 'year', e.target.value)
                        }
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">年</option>
                        {Array.from({ length: 15 }, (_, i) => {
                          const year = 2025 + i;
                          return (
                            <option key={year} value={String(year)}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* 旅行护照是否包含美国签证 - 新增 */}
                  <div className="md:col-span-2 border-t pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      您的美国签证在: *
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="passport_contains_visa"
                          checked={formData.travel_document.passport_contains_visa === true}
                          onChange={() =>
                            handleInputChange('travel_document.passport_contains_visa', true)
                          }
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-gray-700">新护照</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="passport_contains_visa"
                          checked={formData.travel_document.passport_contains_visa === false}
                          onChange={() =>
                            handleInputChange('travel_document.passport_contains_visa', false)
                          }
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-gray-700">旧护照</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 条件显示：旧护照信息 - 当passport_contains_visa=false时显示 */}
            {formData.travel_document.has_10year_visa &&
             formData.travel_document.passport_contains_visa === false && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-5 my-4 rounded space-y-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-yellow-900 mb-2">
                      旧护照信息（持有美国签证的护照）
                    </h4>
                    <p className="text-sm text-yellow-800 mb-4">
                      您的美国签证在旧护照上，请填写持有签证的旧护照详细信息
                    </p>

                    <div className="space-y-4">
                      {/* 旧护照姓氏 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          旧护照姓氏（英文）*
                        </label>
                        <input
                          type="text"
                          value={formData.travel_document.visa_passport_surname || ''}
                          onChange={(e) =>
                            handleInputChange('travel_document.visa_passport_surname', e.target.value)
                          }
                          placeholder="例: ZHANG"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* 旧护照名字 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          旧护照名字（英文）*
                        </label>
                        <input
                          type="text"
                          value={formData.travel_document.visa_passport_given_name || ''}
                          onChange={(e) =>
                            handleInputChange('travel_document.visa_passport_given_name', e.target.value)
                          }
                          placeholder="例: SAN"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* 旧护照出生日期 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          旧护照出生日期 (DD/MM/YYYY) *
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <select
                            value={formData.travel_document.visa_passport_date_of_birth?.day || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_date_of_birth', 'day', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">日</option>
                            {Array.from({ length: 31 }, (_, i) => (
                              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                {String(i + 1).padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.travel_document.visa_passport_date_of_birth?.month || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_date_of_birth', 'month', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">月</option>
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                {String(i + 1).padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.travel_document.visa_passport_date_of_birth?.year || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_date_of_birth', 'year', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">年</option>
                            {Array.from({ length: 100 }, (_, i) => {
                              const year = 2025 - i;
                              return (
                                <option key={year} value={String(year)}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      {/* 旧护照号码 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          旧护照号码 *
                        </label>
                        <input
                          type="text"
                          value={formData.travel_document.visa_passport_number || ''}
                          onChange={(e) =>
                            handleInputChange('travel_document.visa_passport_number', e.target.value)
                          }
                          placeholder="例: E123456789"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* 旧护照签发国家 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          旧护照签发国家 *
                        </label>
                        <select
                          value={formData.travel_document.visa_passport_country || 'CN'}
                          onChange={(e) =>
                            handleInputChange('travel_document.visa_passport_country', e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">选择国家</option>
                          <option value="CN">中国</option>
                        </select>
                      </div>

                      {/* 旧护照签发日期 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          旧护照签发日期 (DD/MM/YYYY) *
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <select
                            value={formData.travel_document.visa_passport_issue_date?.day || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_issue_date', 'day', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">日</option>
                            {Array.from({ length: 31 }, (_, i) => (
                              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                {String(i + 1).padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.travel_document.visa_passport_issue_date?.month || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_issue_date', 'month', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">月</option>
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                {String(i + 1).padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.travel_document.visa_passport_issue_date?.year || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_issue_date', 'year', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">年</option>
                            {Array.from({ length: 50 }, (_, i) => {
                              const year = 2025 - i;
                              return (
                                <option key={year} value={String(year)}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      {/* 旧护照过期日期 */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          旧护照过期日期 (DD/MM/YYYY) *
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <select
                            value={formData.travel_document.visa_passport_expiry_date?.day || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_expiry_date', 'day', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">日</option>
                            {Array.from({ length: 31 }, (_, i) => (
                              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                {String(i + 1).padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.travel_document.visa_passport_expiry_date?.month || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_expiry_date', 'month', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">月</option>
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                                {String(i + 1).padStart(2, '0')}
                              </option>
                            ))}
                          </select>
                          <select
                            value={formData.travel_document.visa_passport_expiry_date?.year || ''}
                            onChange={(e) =>
                              handleDateChange('travel_document.visa_passport_expiry_date', 'year', e.target.value)
                            }
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                          >
                            <option value="">年</option>
                            {Array.from({ length: 50 }, (_, i) => {
                              const year = 2025 + 10 - i;
                              return (
                                <option key={year} value={String(year)}>
                                  {year}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCurrentStep(0)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                上一步
              </button>
              <button
                onClick={() => {
                  if (validateStep1()) {
                    // 自动复制Step 2的旅行证件信息到Step 3的申请人信息
                    copyTravelDocToApplicant();
                    setCurrentStep(2);
                  }
                }}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                继续
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Enrollee Information - 最复杂的一步 */}
        {currentStep === 2 && (
          <div className="space-y-6">
            {/* 自动填充提示 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                ℹ️ <strong>贴心提示:</strong> 您在Step 2中输入的姓名和出生日期已自动填充到下方。如果护照名与法律名不同，您可以修改。
              </p>
            </div>

            {/* 个人基本信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                个人基本信息
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      姓氏 *
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.surname}
                      onChange={(e) => handleInputChange('applicant.surname', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      名字 *
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.given_name}
                      onChange={(e) => handleInputChange('applicant.given_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* 本国语言名字（中文汉字）- 必填 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      姓（汉字）*
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.surname_native_language}
                      onChange={(e) =>
                        handleInputChange('applicant.surname_native_language', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="例: 张"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      名（汉字）*
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.given_name_native_language}
                      onChange={(e) =>
                        handleInputChange('applicant.given_name_native_language', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="例: 三"
                    />
                  </div>
                </div>

                {/* 性别和出生日期 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      性别 *
                    </label>
                    <select
                      value={formData.applicant.sex}
                      onChange={(e) => handleInputChange('applicant.sex', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="M">男性</option>
                      <option value="F">女性</option>
                    </select>
                  </div>
                </div>

                {/* 出生日期 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    出生日期 (DD/MM/YYYY) *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <select
                      value={formData.applicant.date_of_birth.day}
                      onChange={(e) =>
                        handleDateChange('applicant.date_of_birth', 'day', e.target.value)
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">日</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <select
                      value={formData.applicant.date_of_birth.month}
                      onChange={(e) =>
                        handleDateChange('applicant.date_of_birth', 'month', e.target.value)
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">月</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={String(i + 1).padStart(2, '0')}>
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <select
                      value={formData.applicant.date_of_birth.year}
                      onChange={(e) =>
                        handleDateChange('applicant.date_of_birth', 'year', e.target.value)
                      }
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">年</option>
                      {Array.from({ length: 125 }, (_, i) => {
                        const year = 2025 - i;
                        return (
                          <option key={year} value={String(year)}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* 出生地 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      出生城市 *
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.city_of_birth}
                      onChange={(e) => handleInputChange('applicant.city_of_birth', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="如不知道填UNKNOWN"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      出生国家 *
                    </label>
                    <select
                      value={formData.applicant.country_of_birth}
                      onChange={(e) =>
                        handleInputChange('applicant.country_of_birth', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CN">中国</option>
                      <option value="US">美国</option>
                      <option value="GB">英国</option>
                      {/* 更多国家选项... */}
                    </select>
                  </div>
                </div>

                {/* 国籍 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    国籍 *
                  </label>
                  <select
                    value={formData.applicant.country_of_citizenship}
                    onChange={(e) =>
                      handleInputChange('applicant.country_of_citizenship', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CN">中国</option>
                    <option value="US">美国</option>
                    <option value="GB">英国</option>
                    {/* 更多国家选项... */}
                  </select>
                </div>

                {/* ✅ 修复 (2025-11-12)：国民身份证号码字段 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    身份证号
                  </label>
                  <input
                    type="text"
                    value={formData.applicant.national_identification_number || ''}
                    onChange={(e) => handleInputChange('applicant.national_identification_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="中国公民填18位身份证号，不知道填UNKNOWN"
                    maxLength="30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    提示: 如果没有身份证号码，系统将自动填充"UNKNOWN"
                  </p>
                </div>
              </div>
            </div>

            {/* 家庭地址 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                家庭地址
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    地址第一行 *
                  </label>
                  <input
                    type="text"
                    value={formData.applicant.address_line1}
                    onChange={(e) => handleInputChange('applicant.address_line1', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="街道号和街道名"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      地址第二行
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.address_line2}
                      onChange={(e) => handleInputChange('applicant.address_line2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="楼号等"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      公寓号
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.apartment_number}
                      onChange={(e) => handleInputChange('applicant.apartment_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      城市 *
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.city}
                      onChange={(e) => handleInputChange('applicant.city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="例: Beijing"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      州/省/地区 *
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.state_province_region}
                      onChange={(e) => handleInputChange('applicant.state_province_region', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="例: Beijing"
                    />
                  </div>
                </div>

                {/* 地址本国语言版本 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    地址（中文）
                  </label>
                  <input
                    type="text"
                    value={formData.applicant.home_address_native_language || ''}
                    onChange={(e) => handleInputChange('applicant.home_address_native_language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 北京市朝阳区建国路1号"
                  />
                </div>
              </div>
            </div>

            {/* 联系信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                联系信息
              </h3>

              <div className="space-y-4">
                {/* 电话号码（支持"Add Another"） */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    电话号码 *
                  </label>
                  {formData.applicant.phone_numbers.map((phone, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <select
                        value={phone.phone_type}
                        onChange={(e) => {
                          const newData = JSON.parse(JSON.stringify(formData));
                          newData.applicant.phone_numbers[idx].phone_type = e.target.value;
                          setConfirmApplicantPhone(false);
                          setFormData(newData);
                        }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="Home">家庭</option>
                        <option value="Work">工作</option>
                        <option value="Mobile">移动</option>
                        <option value="Other">其他</option>
                      </select>
                      <select
                        value={phone.country_code}
                        onChange={(e) => {
                          const newData = JSON.parse(JSON.stringify(formData));
                          newData.applicant.phone_numbers[idx].country_code = e.target.value;
                          setConfirmApplicantPhone(false);
                          setFormData(newData);
                        }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="CN:86">+86 (中国)</option>
                        <option value="US:1">+1 (美国)</option>
                        <option value="GB:44">+44 (英国)</option>
                      </select>
                      <input
                        type="text"
                        value={phone.number}
                        onChange={(e) => {
                          const newData = JSON.parse(JSON.stringify(formData));
                          // 仅保留数字，限制到最长20位
                          const digits = (e.target.value || '').replace(/\\D/g, '').slice(0, 20);
                          newData.applicant.phone_numbers[idx].number = digits;
                          setConfirmApplicantPhone(false);
                          setFormData(newData);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="电话号码（仅输入数字）"
                      />
                      {idx > 0 && (
                        <button
                          onClick={() => removePhoneNumber(idx)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {/* 本地确认复选框：允许在格式校验不过时继续提交 */}
                  <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={confirmApplicantPhone}
                      onChange={(e) => setConfirmApplicantPhone(e.target.checked)}
                    />
                    <span>我确认该电话号码正确</span>
                  </label>
                  <button
                    onClick={addPhoneNumber}
                    className="mt-2 flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Plus className="w-4 h-4" />
                    添加另一个电话
                  </button>
                </div>

                {/* 邮箱 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      邮箱 *
                    </label>
                    <input
                      type="email"
                      value={formData.applicant.primary_email.email}
                      onChange={(e) => {
                        const newData = JSON.parse(JSON.stringify(formData));
                        newData.applicant.primary_email.email = e.target.value;
                        setFormData(newData);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      确认邮箱 *
                    </label>
                    <input
                      type="email"
                      value={formData.applicant.primary_email.email_confirm}
                      onChange={(e) => {
                        const newData = JSON.parse(JSON.stringify(formData));
                        newData.applicant.primary_email.email_confirm = e.target.value;
                        setFormData(newData);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 别名信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                别名信息
              </h3>

              <div className="space-y-4">
                {/* Has Aliases 下拉菜单 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    是否有别名或其他名字 *
                  </label>
                  <select
                    value={formData.applicant.has_aliases ? 'Yes' : 'No'}
                    onChange={(e) => {
                      const newData = JSON.parse(JSON.stringify(formData));
                      newData.applicant.has_aliases = e.target.value === 'Yes';
                      if (!newData.applicant.has_aliases) {
                        newData.applicant.aliases = [];
                      }
                      setFormData(newData);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* 条件显示：别名列表 */}
                {formData.applicant.has_aliases && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      别名列表（最多10个）
                    </label>
                    {formData.applicant.aliases.map((alias, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          type="text"
                          value={alias}
                          onChange={(e) => {
                            const newData = JSON.parse(JSON.stringify(formData));
                            newData.applicant.aliases[idx] = e.target.value;
                            setFormData(newData);
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder={`别名 ${idx + 1}`}
                        />
                        <button
                          onClick={() => {
                            const newData = JSON.parse(JSON.stringify(formData));
                            newData.applicant.aliases.splice(idx, 1);
                            setFormData(newData);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    {formData.applicant.aliases.length < 10 && (
                      <button
                        onClick={() => {
                          const newData = JSON.parse(JSON.stringify(formData));
                          newData.applicant.aliases.push('');
                          setFormData(newData);
                        }}
                        className="mt-2 flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        添加另一个别名
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* 其他国籍信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                其他国籍信息
              </h3>

              <div className="space-y-4">
                {/* 现在是否是其他国家公民 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    您现在是否是其他国家的公民或国民？*
                  </label>
                  <select
                    value={formData.applicant.is_citizen_of_another_country_now ? 'Yes' : 'No'}
                    onChange={(e) =>
                      handleInputChange('applicant.is_citizen_of_another_country_now', e.target.value === 'Yes')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* 曾经是否是其他国家公民 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    您曾经是否是其他国家的公民或国民？*
                  </label>
                  <select
                    value={formData.applicant.has_ever_been_citizen_of_another_country ? 'Yes' : 'No'}
                    onChange={(e) =>
                      handleInputChange('applicant.has_ever_been_citizen_of_another_country', e.target.value === 'Yes')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* 是否持有其他护照/ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    您是否持有其他国家签发的护照或身份证？*
                  </label>
                  <select
                    value={formData.applicant.has_other_passport_or_id ? 'Yes' : 'No'}
                    onChange={(e) =>
                      handleInputChange('applicant.has_other_passport_or_id', e.target.value === 'Yes')
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
              </div>
            </div>

            {/* 社交媒体信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                社交媒体信息
              </h3>

              <div className="space-y-4">
                {/* No online presence 复选框 */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.applicant.no_online_presence || false}
                    onChange={(e) => {
                      const newData = JSON.parse(JSON.stringify(formData));
                      newData.applicant.no_online_presence = e.target.checked;
                      newData.applicant.has_online_presence = !e.target.checked;
                      setFormData(newData);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    我没有在线媒体账户
                  </label>
                </div>

                {/* 条件显示：社交媒体输入 */}
                {!formData.applicant.no_online_presence && (
                  <>
                    {/* 预定义平台 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Facebook Page ID
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.facebook || ''}
                          onChange={(e) => handleInputChange('applicant.facebook', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: john.doe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          LinkedIn Profile Link
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.linkedin || ''}
                          onChange={(e) => handleInputChange('applicant.linkedin', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: linkedin.com/in/john-doe"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Twitter User ID
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.twitter || ''}
                          onChange={(e) => handleInputChange('applicant.twitter', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: @johndoe"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Instagram User ID
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.instagram || ''}
                          onChange={(e) => handleInputChange('applicant.instagram', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: @johndoe"
                        />
                      </div>
                    </div>

                    {/* 其他社交媒体平台 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        其他社交媒体账户
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        例如：微信（WeChat）、QQ
                      </p>
                      {formData.applicant.social_media_profiles && formData.applicant.social_media_profiles.map((profile, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={profile.platform || ''}
                            onChange={(e) => {
                              const newData = JSON.parse(JSON.stringify(formData));
                              newData.applicant.social_media_profiles[idx].platform = e.target.value;
                              setFormData(newData);
                            }}
                            className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="平台名称"
                          />
                          <input
                            type="text"
                            value={profile.identifier || ''}
                            onChange={(e) => {
                              const newData = JSON.parse(JSON.stringify(formData));
                              newData.applicant.social_media_profiles[idx].identifier = e.target.value;
                              setFormData(newData);
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="账户标识"
                          />
                          <button
                            onClick={() => {
                              const newData = JSON.parse(JSON.stringify(formData));
                              newData.applicant.social_media_profiles.splice(idx, 1);
                              setFormData(newData);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}

                      {formData.applicant.social_media_profiles && formData.applicant.social_media_profiles.length < 1 && (
                        <button
                          onClick={() => {
                            const newData = JSON.parse(JSON.stringify(formData));
                            newData.applicant.social_media_profiles.push({ platform: '', identifier: '' });
                            setFormData(newData);
                          }}
                          className="mt-2 flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          添加另一个社交媒体账户
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* GE会员信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                全球通行证会员信息
              </h3>

              <div className="space-y-4">
                {/* Is GE Member */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    您是否是CBP全球通行证计划成员？
                  </label>
                  <select
                    value={formData.applicant.is_ge_member ? 'Yes' : 'No'}
                    onChange={(e) => {
                      const newData = JSON.parse(JSON.stringify(formData));
                      newData.applicant.is_ge_member = e.target.value === 'Yes';
                      if (!newData.applicant.is_ge_member) {
                        newData.applicant.ge_membership_number = '';
                      }
                      setFormData(newData);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* 条件显示：GE会员号 */}
                {formData.applicant.is_ge_member && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      全球通行证会员号 *
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.ge_membership_number || ''}
                      onChange={(e) => handleInputChange('applicant.ge_membership_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="请输入会员号"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 雇佣信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                雇佣信息
              </h3>

              <div className="space-y-4">
                {/* Has Employer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    您是否有现任或前任雇主？
                  </label>
                  <select
                    value={formData.applicant.has_current_previous_employer ? 'Yes' : 'No'}
                    onChange={(e) => {
                      const newData = JSON.parse(JSON.stringify(formData));
                      newData.applicant.has_current_previous_employer = e.target.value === 'Yes';
                      if (!newData.applicant.has_current_previous_employer) {
                        newData.applicant.employer_name = '';
                        newData.applicant.employer_occupation = '';
                        newData.applicant.employer_address_line1 = '';
                        newData.applicant.employer_city = '';
                        newData.applicant.employer_address_state = '';
                        newData.applicant.employer_country = 'CN';
                      }
                      setFormData(newData);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>

                {/* 条件显示：雇主详细信息 */}
                {formData.applicant.has_current_previous_employer && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          雇主名称 *
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.employer_name || ''}
                          onChange={(e) => handleInputChange('applicant.employer_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: ABC Company"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          职业 *
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.employer_occupation || ''}
                          onChange={(e) => handleInputChange('applicant.employer_occupation', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: Software Engineer"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        雇主地址 *
                      </label>
                      <input
                        type="text"
                        value={formData.applicant.employer_address_line1 || ''}
                        onChange={(e) => handleInputChange('applicant.employer_address_line1', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="街道号和街道名"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          雇主城市 *
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.employer_city || ''}
                          onChange={(e) => handleInputChange('applicant.employer_city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: Beijing"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          州/省/地区 *
                        </label>
                        <input
                          type="text"
                          value={formData.applicant.employer_address_state || ''}
                          onChange={(e) => handleInputChange('applicant.employer_address_state', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="例: Beijing（或州名）"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        雇主国家 *
                      </label>
                      <select
                        value={formData.applicant.employer_country || 'CN'}
                        onChange={(e) => handleInputChange('applicant.employer_country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="CN">中国</option>
                        <option value="US">美国</option>
                        <option value="GB">英国</option>
                        <option value="JP">日本</option>
                        <option value="KR">韩国</option>
                        {/* 更多国家选项可根据需要添加 */}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* 父母信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                父母信息
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      父亲姓氏
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.father_surname}
                      onChange={(e) => handleInputChange('applicant.father_surname', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="不知道则填UNKNOWN"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      父亲名字
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.father_given_name}
                      onChange={(e) => handleInputChange('applicant.father_given_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="不知道则填UNKNOWN"
                    />
                  </div>
                </div>

                {/* 父亲本国语言名字 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    父亲名字（本国语言）
                  </label>
                  <input
                    type="text"
                    value={formData.applicant.father_name_native_language || ''}
                    onChange={(e) => handleInputChange('applicant.father_name_native_language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 明华"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      母亲姓氏
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.mother_surname}
                      onChange={(e) => handleInputChange('applicant.mother_surname', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="不知道则填UNKNOWN"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      母亲名字
                    </label>
                    <input
                      type="text"
                      value={formData.applicant.mother_given_name}
                      onChange={(e) => handleInputChange('applicant.mother_given_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="不知道则填UNKNOWN"
                    />
                  </div>
                </div>

                {/* 母亲本国语言名字 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    母亲名字（本国语言）
                  </label>
                  <input
                    type="text"
                    value={formData.applicant.mother_name_native_language || ''}
                    onChange={(e) => handleInputChange('applicant.mother_name_native_language', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例: 秀英"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                上一步
              </button>
              <button
                onClick={() => {
                  if (validateStep2()) {
                    setCurrentStep(3);
                  }
                }}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                继续
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Travel Information */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                美国联系人信息
              </h3>

              <div className="space-y-4">
                {/* ✨ 修复问题4 (2025-12-21)：删除"使用我的个人信息"CopyInfoButton组件，统一用户手填 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名（全名）*
                  </label>
                  <input
                    type="text"
                    value={formData.us_point_of_contact.name}
                    onChange={(e) =>
                      handleInputChange('us_point_of_contact.name', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例: John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    地址第一行 *
                  </label>
                  <input
                    type="text"
                    value={formData.us_point_of_contact.address.address_line1}
                    onChange={(e) =>
                      handleInputChange('us_point_of_contact.address.address_line1', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    地址第二行
                  </label>
                  <input
                    type="text"
                    value={formData.us_point_of_contact.address.address_line2}
                    onChange={(e) =>
                      handleInputChange('us_point_of_contact.address.address_line2', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      城市 *
                    </label>
                    <input
                      type="text"
                      value={formData.us_point_of_contact.address.city}
                      onChange={(e) =>
                        handleInputChange('us_point_of_contact.address.city', e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      州 *
                    </label>
                    <USStateSelect
                      value={formData.us_point_of_contact.address.state_province}
                      onChange={(state) =>
                        handleInputChange('us_point_of_contact.address.state_province', state)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮编 *
                  </label>
                  <input
                    type="text"
                    value={formData.us_point_of_contact.address.postal_code}
                    onChange={(e) =>
                      handleInputChange('us_point_of_contact.address.postal_code', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                    <input
                      type="checkbox"
                      checked={confirmUsContactAddress}
                      onChange={(e) => setConfirmUsContactAddress(e.target.checked)}
                    />
                    <span>我确认该地址正确</span>
                  </label>
                </div>

                {/* 电话信息 */}
                {/* ✨ 修复问题4 (2025-12-21)：删除电话类型字段，调整网格为md:grid-cols-2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      国家代码 *
                    </label>
                    <select
                      value={formData.us_point_of_contact.phone_country_code}
                      onChange={(e) => handleUsContactCountryCodeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="US:1">+1 (美国)</option>
                      <option value="CN:86">+86 (中国)</option>
                      <option value="GB:44">+44 (英国)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      电话号码 *
                    </label>
                    <input
                      type="tel"
                      value={formData.us_point_of_contact.phone_number}
                      onChange={(e) => {
                        const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 20);
                        handleInputChange('us_point_of_contact.phone_number', digits);
                        setConfirmUsContactPhone(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+1 201 255 1234"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      国家代码变更后请重新检查号码；美国号码需为10位数字。
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={confirmUsContactPhone}
                        onChange={(e) => setConfirmUsContactPhone(e.target.checked)}
                      />
                      <span>我确认该电话号码正确</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 pb-4 border-b">
                紧急联系人信息
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名（全名）*
                  </label>
                  <input
                    type="text"
                    value={formData.emergency_contact.name}
                    onChange={(e) =>
                      handleInputChange('emergency_contact.name', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例: Jane Doe"
                  />
                </div>

                {/* 邮箱地址 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮箱地址
                  </label>
                  <input
                    type="email"
                    value={formData.emergency_contact.email || ''}
                    onChange={(e) =>
                      handleInputChange('emergency_contact.email', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="例: emergency@example.com"
                  />
                </div>

                {/* 电话信息 */}
                {/* ✨ 修复 (2025-12-21)：删除电话类型字段，与美国联系人保持一致 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      国家代码 *
                    </label>
                    <select
                      value={formData.emergency_contact.phone_country_code}
                      onChange={(e) => handleEmergencyCountryCodeChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="CN:86">+86 (中国)</option>
                      <option value="US:1">+1 (美国)</option>
                      <option value="GB:44">+44 (英国)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      电话号码 *
                    </label>
                    <input
                      type="tel"
                      value={formData.emergency_contact.phone_number}
                      onChange={(e) => {
                        const digits = (e.target.value || '').replace(/\D/g, '').slice(0, 20);
                        handleInputChange('emergency_contact.phone_number', digits);
                        setConfirmEmergencyPhone(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="+86 137 1234 5678"
                    />
                    <div className="mt-2 text-xs text-gray-500">
                      将按所选国家规则校验号码格式（中国手机号为11位且以1开头）。
                    </div>
                    <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={confirmEmergencyPhone}
                        onChange={(e) => setConfirmEmergencyPhone(e.target.checked)}
                      />
                      <span>我确认该电话号码正确</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                上一步
              </button>
              <button
                onClick={() => {
                  if (validateStep3()) {
                    setCurrentStep(4);
                  }
                }}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                继续
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Eligibility Questions */}
        {currentStep === 4 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-4 border-b">
              Step 5: 资格问题（ELIGIBILITY QUESTIONS）
            </h2>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700">
                <strong>填写说明：</strong>以下问题为美国海关与边境保护局（CBP）EVUS 所需的资格审查。请选择<strong>“是/不是”</strong>。如选择“是”，请按页面提示补充信息。
              </p>
            </div>

            <div className="space-y-4">
              {[
                { key: 'q1_physical_mental_disorder', text: '您是否有身体或精神障碍；或为药物滥用/成瘾者；或当前患有传染性疾病？（Communicable diseases）' },
                { key: 'q2_criminal_record', text: '您是否曾因犯罪被逮捕或定罪，且该犯罪导致财产严重损害，或对他人/政府当局造成严重伤害？' },
                { key: 'q3_drug_violation', text: '您是否曾违反任何与持有、使用或分发非法药物相关的法律？（Illegal drugs）' },
                { key: 'q4_terrorist_activities', text: '您是否意图从事或曾经从事恐怖活动、间谍活动、破坏活动或种族灭绝？（Terrorism/Espionage/Sabotage/Genocide）' },
                { key: 'q5_fraud_false_statement', text: '您是否曾为获取或帮助他人获取美国签证或入境许可而实施欺诈或虚假陈述？（Fraud/Misrepresentation）' },
                { key: 'q6_illegal_work_us', text: '您目前是否在寻求在美国就业，或曾在未获美国政府许可的情况下在美国工作？（Employment without authorization）' },
                { key: 'q7_visa_denied', text: '您是否曾被拒发美国签证或被拒绝入境美国，或曾被撤销美国签证？（Denied U.S. visa/entry or visa canceled）' },
                { key: 'q8_overstayed_us', text: '您是否曾在美国逾期停留，超过美国政府准许的停留期限？（Overstay）' },
                { key: 'q9_dangerous_countries', text: '自 2011 年 3 月 1 日以来，您是否曾前往或停留于以下国家/地区：伊朗、伊拉克、朝鲜、叙利亚、苏丹、利比亚、索马里或也门？' },
              ].map((item) => (
                <label
                  key={item.key}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.eligibility[item.key]}
                    onChange={(e) =>
                      handleInputChange(`eligibility.${item.key}`, e.target.checked)
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-gray-700">{item.text}</span>
                </label>
              ))}
            </div>

            {/* Q7: 当选择“是”时，显示 When/Where 附加字段 */}
            {formData.eligibility.q7_visa_denied === true && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">签证被拒的时间与地点</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      When (DD/MM/YYYY)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <select
                        value={formData.eligibility.q7_when_day}
                        onChange={(e) => handleInputChange('eligibility.q7_when_day', e.target.value)}
                        className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">日</option>
                        {Array.from({ length: 31 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1)}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.eligibility.q7_when_month}
                        onChange={(e) => handleInputChange('eligibility.q7_when_month', e.target.value)}
                        className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">月</option>
                        {Array.from({ length: 12 }, (_, i) => (
                          <option key={i + 1} value={String(i + 1)}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.eligibility.q7_when_year}
                        onChange={(e) => handleInputChange('eligibility.q7_when_year', e.target.value)}
                        className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">年</option>
                        {Array.from({ length: 30 }, (_, i) => {
                          const year = 2025 - i;
                          return (
                            <option key={year} value={String(year)}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Where
                    </label>
                    <input
                      type="text"
                      value={formData.eligibility.q7_where}
                      onChange={(e) => handleInputChange('eligibility.q7_where', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="城市/使领馆等（ASCII 字符）"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  提示：日期与地点用于 EVUS 网站的补充问题，不能为空。
                </p>
              </div>
            )}

            <p className="text-sm text-gray-500 mt-4">
              注: 如果您对任何问题的答案是\"是\",可能会影响您的EVUS批准。请确保所有答案都是真实的。
            </p>

            {/* 费用信息 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
              <div className="flex items-start">
                <DollarSign className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-2">服务费用</h3>
                  <p className="text-blue-800 text-sm mb-2">
                    本服务将消耗您账户中的 <strong>250 积分</strong> 来处理您的EVUS新登记申请。
                  </p>
                  <p className="text-blue-700 text-sm">
                    EVUS官方费用(不含在本服务中): <strong>USD $30.00</strong>
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setCurrentStep(3)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                上一步
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
              >
                返回首页
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              >
                {submitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    提交中...
                  </>
                ) : (
                  '提交申请'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 6: 审核确认 */}
        {currentStep === 5 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-4 border-b">
              Step 6: 审核信息
            </h2>

            {/* 申请人信息摘要 */}
            <div className="bg-gray-50 p-6 rounded-lg space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900">申请人信息</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">姓名:</span>
                    <span className="font-medium ml-2">
                      {formData.applicant?.surname} {formData.applicant?.given_name}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">性别:</span>
                    <span className="font-medium ml-2">
                      {formData.applicant?.sex === 'M' ? '男' : '女'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">出生日期:</span>
                    <span className="font-medium ml-2">
                      {formData.applicant?.date_of_birth?.day}/{formData.applicant?.date_of_birth?.month}/{formData.applicant?.date_of_birth?.year}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">国籍:</span>
                    <span className="font-medium ml-2">
                      {formData.applicant?.country_of_citizenship}
                    </span>
                  </div>
                </div>
              </div>

              {/* 护照信息 */}
              <div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900">护照信息</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">护照号:</span>
                    <span className="font-medium ml-2 font-mono">
                      {formData.travel_document?.passport_number}
                    </span>
                  </div>
                  {formData.travel_document?.has_10year_visa && (
                    <div>
                      <span className="text-gray-600">签证号:</span>
                      <span className="font-medium ml-2 font-mono">
                        {formData.travel_document?.visa_foil_number}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* 联系信息 */}
              <div>
                <h3 className="font-semibold text-lg mb-3 text-gray-900">联系信息</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="col-span-2">
                    <span className="text-gray-600">邮箱:</span>
                    <span className="font-medium ml-2">
                      {formData.applicant?.primary_email?.email}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-600">电话:</span>
                    <span className="font-medium ml-2">
                      {formData.applicant?.phone_numbers?.[0]?.number}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 自动确认提示 */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mt-6">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                <p className="text-blue-800 font-medium">
                  系统正在自动确认您的信息并提交...
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-600 mt-4">
              ℹ️ 无需任何操作，系统将自动完成审核确认并进入支付步骤
            </p>
          </div>
        )}

        {/* Step 7: 支付处理 */}
        {currentStep === 6 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-4 border-b">
              Step 7: 支付处理
            </h2>

            {processing && taskStatus?.status === 'processing' && (
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600"></div>
                  <p className="text-yellow-800 font-medium text-lg">
                    正在处理支付...
                  </p>
                </div>
                <div className="bg-white p-4 rounded border border-yellow-300">
                  <p className="text-xs text-yellow-700 mt-2">
                    此过程可能需要1-2分钟，请耐心等待...
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    任务ID: {taskId}
                  </p>
                </div>
              </div>
            )}

            {taskStatus?.status === 'completed' && (
              <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                <div className="flex items-center space-x-2 mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <h3 className="text-xl font-bold text-green-800">
                    EVUS注册成功！
                  </h3>
                </div>

                <div className="bg-white p-6 rounded border-2 border-green-300 mb-4">
                  <p className="text-sm text-gray-600 mb-2">您的EVUS登记号:</p>
                  <p className="text-3xl font-mono font-bold text-green-700 my-3 bg-green-50 p-4 rounded text-center">
                    {taskStatus.enrollment_number}
                  </p>
                  <p className="text-xs text-gray-500 text-center">
                    请妥善保存此登记号，有效期2年
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 text-sm text-green-800">
                  <p>✅ 支付已完成 (USD $30.00)</p>
                  <p>✅ EVUS登记有效期: 2年</p>
                  <p>✅ 登记号已验证有效</p>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => window.print()}
                    className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium flex items-center justify-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    打印/保存确认页面
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    返回首页
                  </button>
                </div>
              </div>
            )}

            {taskStatus?.status === 'failed' && (
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
                <div className="flex items-center space-x-2 mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                  <h3 className="text-xl font-bold text-red-800">
                    支付处理失败
                  </h3>
                </div>
                <p className="text-red-700 mb-4">
                  {taskStatus.error || '支付过程中出现错误，请联系客服'}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setCurrentStep(4)}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                  >
                    返回修改
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                  >
                    返回首页
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
      <Footer variant="simple" showCopyright={false} />
    </>
  );
}
